import { zodResolver } from '@hookform/resolvers/zod';
import {
  type Account,
  type RecurringTemplate,
  type RecurringTemplateRequest,
  sortCurrencies,
} from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { CalendarClock, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { type FieldError, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { FormError } from '../components/FormError';
import { Chip, Row } from '../components/FormRow';
import { PageHeader } from '../components/PageHeader';
import { PickerSheet } from '../components/PickerSheet';
import { RecurringDueRow } from '../components/RecurringRows';
import { LateNotice } from '../components/HomeCards';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, IconSquare, ListGroup, listRowClass, SectionLabel } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Segmented } from '../components/ui/segmented';
import { parsePositive } from '../lib/add-form';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { formatDay, todayIn } from '../lib/dates';
import { useAccounts, useRecurring } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { formatAmount, groupAmountInput } from '../lib/money';
import {
  type RecurringFormValues,
  recurringFormSchema,
  recurringFormValues,
} from '../lib/recurring-form';
import { CategoryChip, CategoryPicker } from './AddPage';

/** "Every month on day 31" / "Every Friday". */
function useScheduleLine() {
  const { t } = useTranslation();
  return (r: Pick<RecurringTemplate, 'frequency' | 'dayOfMonth' | 'weekday'>) =>
    r.frequency === 'monthly'
      ? t('recurring.everyMonth', { day: r.dayOfMonth })
      : t('recurring.everyWeek', { weekday: t(`recurring.weekdays.${r.weekday ?? 0}`) });
}

/** Recurring transactions: what repeats, when it is next due, and what is due now. */
export function RecurringPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const recurring = useRecurring();
  const scheduleLine = useScheduleLine();
  if (!me.data || !recurring.data) {
    if (recurring.isError) return <FormError error={recurring.error} />;
    return <p role="status">{t('app.loading')}</p>;
  }
  const today = todayIn(me.data.family.timezone);
  const day = (date: string) =>
    formatDay(date, today, i18n.language, {
      today: t('activity.today'),
      yesterday: t('activity.yesterday'),
    });
  const items = recurring.data.items;
  const due = items.flatMap((template) =>
    template.due.map((occurrence) => ({ template, occurrence })),
  );
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('recurring.title')}
        back="/activity"
        action={
          <Link to="/recurring/new" className={buttonVariants({ variant: 'small' })}>
            {t('recurring.add')}
          </Link>
        }
      />
      <LateNotice />
      {items.length === 0 ? (
        <Card className="flex flex-col gap-4">
          <p className="text-muted">{t('recurring.empty')}</p>
          <Link to="/recurring/new" className={buttonVariants()}>
            {t('recurring.add')}
          </Link>
        </Card>
      ) : (
        <>
          {due.length > 0 && (
            <section className="flex flex-col gap-2">
              <SectionLabel>{t('home.dueSoon')}</SectionLabel>
              <ListGroup>
                {due.map(({ template, occurrence }) => (
                  <li key={`${template.id}-${occurrence.dueOn}`}>
                    <RecurringDueRow template={template} occurrence={occurrence} today={today} />
                  </li>
                ))}
              </ListGroup>
            </section>
          )}
          <ListGroup>
            {items.map((r) => (
              <li key={r.id}>
                <Link to={`/recurring/${r.id}`} className={listRowClass}>
                  <IconSquare>
                    <CalendarClock className="size-5" strokeWidth={2} />
                  </IconSquare>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[15px] font-medium">{r.name}</span>
                    <span className="truncate text-xs text-muted">
                      {scheduleLine(r)} ·{' '}
                      {r.paused
                        ? t('recurring.paused')
                        : r.nextDueOn
                          ? t('recurring.next', { date: day(r.nextDueOn) })
                          : t('recurring.ended')}
                      {r.autoRecord ? ` · ${t('recurring.auto')}` : ''}
                    </span>
                  </span>
                  {r.amount && (
                    <span dir="ltr" className="shrink-0 text-[15px] font-semibold tabular-nums">
                      {formatAmount(r.amount, r.currency, i18n.language)}
                    </span>
                  )}
                  <ChevronRight aria-hidden="true" className="size-5 text-muted rtl:rotate-180" />
                </Link>
              </li>
            ))}
          </ListGroup>
        </>
      )}
    </div>
  );
}

/** New recurring transaction (/recurring/new) or edit one (/recurring/:id). */
export function RecurringFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const me = useMe();
  const accounts = useAccounts();
  const recurring = useRecurring();
  if (!me.data || !accounts.data || !recurring.data) {
    if (accounts.isError || recurring.isError) {
      return <FormError error={accounts.error ?? recurring.error} />;
    }
    return <p role="status">{t('app.loading')}</p>;
  }
  const template = id ? recurring.data.items.find((r) => r.id === id) : undefined;
  if (id && !template) {
    return (
      <Card role="alert">
        <p>{t('errors.recurring.not_found')}</p>
      </Card>
    );
  }
  const recordable = accounts.data.items.filter((a) => a.canRecord && a.archivedAt === null);
  if (recordable.length === 0) {
    return (
      <Card className="flex flex-col gap-4">
        <p className="text-muted">{t('add.noAccounts')}</p>
        <Link to="/accounts/new" className={buttonVariants()}>
          {t('add.noAccountsAction')}
        </Link>
      </Card>
    );
  }
  return (
    <RecurringForm
      key={template?.id ?? 'new'}
      template={template}
      accounts={recordable}
      baseCurrency={me.data.family.baseCurrency}
      today={todayIn(me.data.family.timezone)}
    />
  );
}

function RecurringForm({
  template,
  accounts,
  baseCurrency,
  today,
}: {
  template: RecurringTemplate | undefined;
  accounts: Account[];
  baseCurrency: string;
  today: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const byId = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const first = accounts[0]!;
  const [picker, setPicker] = useState<'accountId' | 'toAccountId' | 'category' | null>(null);
  const resolver = useMemo(() => zodResolver(recurringFormSchema()), []);
  const form = useForm<RecurringFormValues, unknown, RecurringTemplateRequest>({
    resolver,
    defaultValues: recurringFormValues(template, {
      accountId: first.id,
      currency: first.balances[0]?.currency ?? baseCurrency,
      today,
    }),
  });
  const v = useWatch({ control: form.control }) as RecurringFormValues;
  const errors = form.formState.errors;
  const errorText = (error: FieldError | undefined) =>
    error?.message ? t(error.message, { currency: v.currency }) : null;

  const account = byId.get(v.accountId);
  const toAccount = byId.get(v.toAccountId);
  const currencies = sortCurrencies(
    (account?.balances.map((b) => b.currency) ?? []).filter(
      (c) =>
        v.kind !== 'transfer' || !toAccount || toAccount.balances.some((b) => b.currency === c),
    ),
    baseCurrency,
  );
  const firstCurrency = currencies[0];
  const currencyFits = currencies.includes(v.currency);
  useEffect(() => {
    if (!currencyFits && firstCurrency) form.setValue('currency', firstCurrency);
  }, [currencyFits, firstCurrency, form]);

  const save = useMutation({
    mutationFn: (body: RecurringTemplateRequest) =>
      template ? api.recurring.update(template.id, body) : api.recurring.create(body),
    onSuccess: async () => {
      await invalidate();
      navigate('/recurring');
    },
  });
  const pause = useMutation({
    mutationFn: (paused: boolean) => api.recurring.setPaused(template!.id, paused),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => api.recurring.remove(template!.id),
    onSuccess: async () => {
      await invalidate();
      navigate('/recurring');
    },
  });

  const reformat = () => {
    const raw = form.getValues('amount');
    if (raw.trim() === '') return;
    const parsed = parsePositive(raw, v.currency);
    if (parsed) form.setValue('amount', groupAmountInput(parsed, v.currency));
  };
  const accountOptions = (exclude?: string) =>
    accounts
      .filter((a) => a.id !== exclude)
      .map((a) => ({
        value: a.id,
        label: a.name,
        hint: a.balances.map((b) => formatAmount(b.balance, b.currency, i18n.language)).join(' · '),
      }));
  const fixedAmount = v.amount.trim() !== '';

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) => save.mutate(body))}
    >
      <PageHeader
        title={template ? t('recurring.editTitle') : t('recurring.newTitle')}
        back="/recurring"
      />
      <Segmented
        label={t('add.kindLabel')}
        options={(['expense', 'income', 'transfer'] as const).map((k) => ({
          value: k,
          label: t(`add.kinds.${k}`),
        }))}
        value={v.kind}
        onChange={(k) => {
          form.setValue('kind', k);
          form.setValue('categoryId', '');
        }}
      />
      <div className="flex flex-col gap-1.5">
        <label htmlFor="rec-name" className="text-[13px] font-medium text-muted">
          {t('recurring.name')}
        </label>
        <Input
          id="rec-name"
          autoComplete="off"
          maxLength={60}
          placeholder={t('recurring.namePlaceholder')}
          aria-invalid={errors.name ? true : undefined}
          {...form.register('name')}
        />
        {errors.name && <p className="text-sm text-danger">{errorText(errors.name)}</p>}
      </div>

      <Card className="flex flex-col items-center gap-3">
        <label htmlFor="rec-amount" className="text-[13px] text-muted">
          {t('recurring.amount')}
        </label>
        <input
          id="rec-amount"
          inputMode={v.currency === 'LBP' ? 'text' : 'decimal'}
          autoComplete="off"
          dir="ltr"
          placeholder={v.currency === 'LBP' ? '500k' : '0.00'}
          aria-describedby="rec-amount-help"
          aria-invalid={errors.amount ? true : undefined}
          className="w-full bg-transparent text-center text-[46px] leading-tight font-semibold tracking-[-1px] tabular-nums placeholder:text-muted focus-visible:outline-none"
          {...form.register('amount', { onBlur: reformat })}
        />
        {currencies.length > 1 ? (
          <div className="w-40">
            <Segmented
              label={t('add.currency')}
              options={currencies.map((c) => ({ value: c, label: c }))}
              value={v.currency}
              onChange={(c) => form.setValue('currency', c)}
            />
          </div>
        ) : (
          <span className="rounded-full bg-surface-2 px-3 py-1 text-sm font-semibold">
            {v.currency}
          </span>
        )}
        <p id="rec-amount-help" className="text-center text-xs text-muted">
          {errors.amount ? (
            <span className="text-sm text-danger">{errorText(errors.amount)}</span>
          ) : (
            t('recurring.amountOptional')
          )}
        </p>
      </Card>

      <ListGroup>
        {v.kind === 'transfer' ? (
          <>
            <Row label={t('recurring.fromAccount')} error={errorText(errors.accountId)}>
              <Chip onClick={() => setPicker('accountId')}>
                {account?.name ?? t('add.pickAccount')}
              </Chip>
            </Row>
            <Row label={t('recurring.toAccount')} error={errorText(errors.toAccountId)}>
              <Chip onClick={() => setPicker('toAccountId')}>
                {toAccount?.name ?? t('add.pickAccount')}
              </Chip>
            </Row>
          </>
        ) : (
          <>
            <Row label={t('recurring.category')} error={errorText(errors.categoryId)}>
              <CategoryChip kind={v.kind} id={v.categoryId} onClick={() => setPicker('category')} />
            </Row>
            <Row label={t('recurring.account')} error={errorText(errors.accountId)}>
              <Chip onClick={() => setPicker('accountId')}>
                {account?.name ?? t('add.pickAccount')}
              </Chip>
            </Row>
            <Row label={t('recurring.payee')} htmlFor="rec-payee">
              <input
                id="rec-payee"
                autoComplete="off"
                maxLength={80}
                placeholder={t('add.payeePlaceholder')}
                className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] placeholder:text-muted focus-visible:outline-none"
                {...form.register('payeeName')}
              />
            </Row>
          </>
        )}
        <Row label={t('recurring.note')} htmlFor="rec-note">
          <input
            id="rec-note"
            autoComplete="off"
            maxLength={200}
            placeholder={t('add.notePlaceholder')}
            className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] placeholder:text-muted focus-visible:outline-none"
            {...form.register('description')}
          />
        </Row>
      </ListGroup>

      <section className="flex flex-col gap-2">
        <SectionLabel>{t('recurring.repeats')}</SectionLabel>
        <Segmented
          label={t('recurring.repeats')}
          options={[
            { value: 'monthly', label: t('recurring.monthly') },
            { value: 'weekly', label: t('recurring.weekly') },
          ]}
          value={v.frequency}
          onChange={(f) => form.setValue('frequency', f as 'monthly' | 'weekly')}
        />
        <ListGroup>
          {v.frequency === 'monthly' ? (
            <Row
              label={t('recurring.dayOfMonth')}
              htmlFor="rec-day"
              error={errorText(errors.dayOfMonth)}
            >
              <input
                id="rec-day"
                inputMode="numeric"
                autoComplete="off"
                maxLength={2}
                dir="ltr"
                className="min-h-11 w-16 rounded-full bg-surface-2 px-3 text-center text-sm font-medium tabular-nums focus-visible:outline-2 focus-visible:outline-accent"
                {...form.register('dayOfMonth')}
              />
            </Row>
          ) : (
            <Row label={t('recurring.weekday')} htmlFor="rec-weekday">
              <select
                id="rec-weekday"
                className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
                {...form.register('weekday')}
              >
                {['1', '2', '3', '4', '5', '6', '0'].map((d) => (
                  <option key={d} value={d}>
                    {t(`recurring.weekdays.${d}`)}
                  </option>
                ))}
              </select>
            </Row>
          )}
          <Row label={t('recurring.startOn')} htmlFor="rec-start" error={errorText(errors.startOn)}>
            <input
              id="rec-start"
              type="date"
              className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
              {...form.register('startOn')}
            />
          </Row>
          <Row label={t('recurring.endOn')} htmlFor="rec-end" error={errorText(errors.endOn)}>
            <input
              id="rec-end"
              type="date"
              aria-describedby="rec-end-hint"
              className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
              {...form.register('endOn')}
            />
          </Row>
        </ListGroup>
        {v.frequency === 'monthly' && (
          <p className="px-1 text-xs text-muted">{t('recurring.dayOfMonthHint')}</p>
        )}
        <p id="rec-end-hint" className="px-1 text-xs text-muted">
          {t('recurring.endHint')}
        </p>
      </section>

      <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-3xl border border-line bg-surface px-4 py-3">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="font-medium">{t('recurring.autoRecord')}</span>
          <span id="rec-auto-hint" className="text-sm text-muted">
            {t('recurring.autoRecordHint')}
          </span>
        </span>
        <input
          type="checkbox"
          role="switch"
          aria-describedby="rec-auto-hint"
          className="size-5 shrink-0 accent-accent"
          disabled={!fixedAmount && !v.autoRecord}
          {...form.register('autoRecord')}
        />
      </label>

      {template && <p className="px-1 text-xs text-muted">{t('recurring.changeHint')}</p>}
      <FormError error={save.error ?? pause.error ?? remove.error} />
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? t('common.saving') : t('recurring.save')}
      </Button>
      {template && (
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="secondary"
            disabled={pause.isPending}
            onClick={() => pause.mutate(!template.paused)}
          >
            {template.paused ? t('recurring.resume') : t('recurring.pause')}
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(t('recurring.deleteConfirm', { name: template.name }))) {
                remove.mutate();
              }
            }}
          >
            {t('recurring.delete')}
          </Button>
        </div>
      )}

      {(picker === 'accountId' || picker === 'toAccountId') && (
        <PickerSheet
          title={t(picker === 'toAccountId' ? 'add.pickTo' : 'add.pickAccount')}
          options={accountOptions(picker === 'toAccountId' ? v.accountId : undefined)}
          selected={v[picker]}
          onPick={(value) => {
            form.setValue(picker, value);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === 'category' && v.kind !== 'transfer' && (
        <CategoryPicker
          kind={v.kind}
          selected={v.categoryId}
          onPick={(value) => {
            form.setValue('categoryId', value);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </form>
  );
}
